import { globals } from 'config/globals';
import { RepellType } from 'app/enum/Particles';
import { particles } from 'app/contracts/Particles';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import { ImagePixelDataGrid } from 'utils/ImagePixelDataGrid';
import HomeColours from 'app/views/home/config/HomeColours';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';

export class RadialHalftoneImage {
	//Shared scene-state bag (ScreensDepartment passes its own reference) — read
	//for the virtual-canvas ratio (ex globals.virtualWidth/virtualHeight):
	private _sceneProps: particles.SceneProps;

	private points: RadialHalftoneParticle[] = [];
	private ROOT_2 = Math.sqrt(2);
	private TAU = Math.PI * 2;

	constructor(sceneProps: particles.SceneProps) {
		this._sceneProps = sceneProps;
	}

	private particlePool: Array<HomeParticle>;
	private poolIndex: number = 0;
	public offsetX: number = 0;
	public offsetY: number = 0;
	public fillStyle: string = 'additive';
	private scaleLookups: Array<Array<number>> = [];
	private imageData: ImagePixelDataGrid;
	private _colourTarget;

	private channelFillStyles = {
		additive: {
			red: '#FF0000',
			green: '#00FF00',
			blue: '#0000FF',
			lum: '#FFF'
		},
		subtractive: {
			red: '#00FFFF',
			green: '#FF00FF',
			blue: '#FFFF00',
			lum: '#000'
		}
	};

	public setImgData(imageData: ImagePixelDataGrid | any) {
		this.imageData = imageData;
	}

	public setPool(particlePool: Array<HomeParticle>) {
		this.particlePool = particlePool;
	}

	public create(w: number, h: number, gridSize: number) {
		this.halftoneGrid(w, h, gridSize);
	}

	public tick() {
		this.processInitParticle();
	}

	public offY() {
		for (let i = 0; i < this.points.length; i++) {
			this.points[i].particle.animatedProps._y.setVectorConfig(true, 1, 10);

			this.points[i].particle.animatedProps.setEase({
				x: 20,
				y: 20,
				scale: 30
			});

			this.points[i].particle.animatedProps.stopBreathing();

			this.points[i].particle.animatedProps.targetScale = 0.1 + Math.random() * 0.2;
			this.points[i].particle.repell.active = false;

			let xd = Math.random() < 0.5 ? -1 : 1;
			let xDiff = xd * (Math.random() * 0.01);
			this.points[i].particle.position.set({
				x: this.points[i].particle.position.x + xDiff,
				y: 0
			});
		}
	}

	private halftoneGrid(w: number, h: number, gridSize: number, channels: Array<string> = ['red', 'green', 'blue']) {
		Math.random();

		let scaleCorrect = 1 + (1 - globals.screensZoneScale) * 1;
		scaleCorrect = 1;


		w *= scaleCorrect;
		h *= scaleCorrect;


		HomeColours.dynamicSets.halftone = [
			this.channelFillStyles[this.fillStyle].green,
			this.channelFillStyles[this.fillStyle].red
		];

		if (globals.colourPhase === 'yellow') this._colourTarget = HomeColours.dynamicSets.differ;
		else this._colourTarget = HomeColours.graduated;



		HomeScreens.setAmbientColours(this._colourTarget);

		this.poolIndex = 0;
		for (var i = 0; i < channels.length; i++) {
			this.createGrid(channels[i], w, h, gridSize);
		}
	}

	private createGrid(channel: string, w: number, h: number, gridSize: number) {
		var diag = Math.max(w, h) * this.ROOT_2;
		var angle = 0;
		var halfW = w / 2;
		var halfH = h / 2;
		var offset = gridSize;
		var centerX = halfW + Math.cos(angle) * offset;
		var centerY = halfH + Math.sin(angle) * offset;

		this._initConfig = [];

		var maxLevel = Math.ceil((diag + offset) / gridSize);

		for (var level = 0; level < maxLevel; level++) {
			var max = level * 6 || 1;

			if (!this.scaleLookups[level]) {
				this.scaleLookups[level] = [];
			}

			for (var j = 0; j < max; j++) {
				//Bare call kept for side-effect parity (result was never used):
				HomeColours.getRandomColour(this._colourTarget);

				var maxRadius = centerX + Math.cos(0) * (maxLevel - 1) * gridSize;

				var theta = (this.TAU * j) / max + angle;
				var x = centerX + Math.cos(theta) * level * gridSize;
				var yRaw = centerY + Math.sin(theta) * level * gridSize;

				//Ex globals.virtualWidth / globals.virtualHeight — same metrics
				//off sceneProps.sizes (container.w/h = virtual width/height):
				var y = yRaw * (this._sceneProps.sizes.container.w / this._sceneProps.sizes.container.h);

				//Getting between -1(max-radius) to 1 when 0.5 w/h. Assume to allow for
				//Want to normalise between -0.5 and 0.5 to create/center the image
				//So when -1 x then need to reduce overspill.


				//Only want to do say 70% on y
				//So need correct the range

				//Should be active in 0.15 to 0.85 width
				//Now have a correct 0-1 to value based
				//Need to convert to value that is 0.15 to 0.85
				//I.e 1st 15% should be minus values

				let newRangeRatioX = this.clipImageArea(maxRadius, w, x, 0.1, 0.1);
				let newRangeRatioY = this.clipImageArea(maxRadius, h, yRaw, 0.23, 0.23);

				let baseScale =
					this.scaleLookups[level][j] ||
					this.imageData.getPixelGreyByRatio(newRangeRatioX, newRangeRatioY, -0.1, -0.08);

				//Seeing values of -0.55 to 0.55
				//Want this normalised to
				//Want -0.55 to be
				//x 0.05814845419464934 y 0.5920491259858569 halfW 0.0275 halfH 0.0275 w 0.055 h 0.055
				//ImagePixelDataGrid.ts:31 1.557244621720897 11.264529563379217 479 280


				this._initConfig.push({
					channel: channel,
					x: x,
					y: y,
					baseScale: baseScale,
					level: level,
					j: j,
					counter: 1 + Math.random() * 8
				});
			}
		}
	}

	private _initConfig: Array<any> = [];

	private processInitParticle() {
		let reduce = (window as any).deltaTime;
		let i = 0;
		let colour, level, j;

		while (i < this._initConfig.length) {
			this._initConfig[i].counter -= reduce;

			if (this._initConfig[i].counter <= 0) {
				colour = HomeColours.getRandomColour(this._colourTarget);
				level = this._initConfig[i].level;
				j = this._initConfig[i].j;

				let point = this.initParticle(
					this._initConfig[i].channel,
					this._initConfig[i].x,
					this._initConfig[i].y,
					colour,
					this._initConfig[i].baseScale
				);

				if (point && !this.scaleLookups[level][j]) {
					this.scaleLookups[level][j] = point.baseScale;
				}

				this._initConfig.splice(i, 1);
			} else {
				i++;
			}
		}
	}


	private clipImageArea(maxCircleRadius: number, gridWidth: number, n: number, clip1: number = 0, clip2: number = 0) {
		let range = maxCircleRadius * 2;
		let halfH = gridWidth / 2;
		var rangeCorrect = range - halfH * 2;
		let value = (n + maxCircleRadius) / range - rangeCorrect;

		if (!clip1 && !clip2) {
			return value;
		}

		//Now have a correct 0-1 to value based
		//Need to convert to value that is 0.15 to 0.9
		//I.e 1st 15% should be minus values
		var ratio = 1 - clip1 - clip2;
		var valueClipped = value - clip1;
		return valueClipped / ratio;

	}

	private initParticle(
		channel: string,
		x: number,
		y: number,
		colour: string,
		baseScale: number = null
	): RadialHalftoneParticle {
		let particle: HomeParticle = this.getNextHomeParticle();

		if (particle) {
			let point = new RadialHalftoneParticle();
			point.particle = particle;
			point.channel = channel;
			point.create(x, y, this.offsetX, this.offsetY, colour, baseScale);

			this.points.push(point);

			return point;
		}

		return null;
	}

	private getNextHomeParticle() {
		let particle = this.particlePool[this.poolIndex];
		this.poolIndex++;
		return particle;
	}
}

class RadialHalftoneParticle {
	public particle: HomeParticle;
	public channel: string;
	public x: number;
	public y: number;
	public baseScale: number;

	public create(x: number, y: number, offsetX: number, offsetY: number, colour: string, baseScale: number = null) {
		this.particle.deactivateAllBehaviours(true);

		let offsets = {
			red: { x: 0.00025, y: 0.0015 },
			green: { x: 0.00025, y: -0.0015 },
			blue: { x: -0.00025, y: -0.0015 }
		};

		this.baseScale = baseScale;

		//Hard scale reset:
		this.particle.animatedProps.hardSetScale(Math.random() * 1.2 * this.baseScale || 0.1, this.baseScale);

		this.particle.animatedProps.scale = 0;

		//Start the particles breathing:
		this.particle.animatedProps.startBreathing(
			this.baseScale,
			0.01 * this.baseScale,
			0.4 * this.baseScale,
			[-1, 1],
			Math.random() * 0.4 * this.baseScale,
			Math.random() < 0.5 ? -1 : 1
		);

		this.particle.animatedProps.setVectorStyleMovement(true);
		this.particle.animatedProps.setEase({
			x: 20,
			y: 20
		});


		this.particle.animatedProps.alpha = this.particle.animatedProps.targetAlpha = 1;
		this.particle.activate();
		this.x = x + offsetX + offsets[this.channel].x;
		this.y = y + offsetY + offsets[this.channel].y;

		let startX = Math.random() * (0.008 * this.baseScale);
		Math.random() * 0.0;
		this.particle.position.set({ x: this.x + startX, y: this.y }, true);
		this.particle.position.set({ x: this.x, y: this.y });
		this.particle.colour.set(colour);
		this.particle.particleProps.sprite.blendMode = 'add';

		this.particle.repell.active = true;
		this.particle.repell.type = RepellType.PUSH;
		this.particle.repell.radius = 300;
		this.particle.repell.randomisePower(30, 200);

		let rand = Math.random();

		let partileTexture = 'solid2.png';
		if (rand < 0.0) partileTexture = 'solid-square.png';
		else if (rand < 0.04) partileTexture = 'solid-ring.png';

		this.particle.changeParticle(partileTexture);
	}
}
